import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { CanvasAuxState } from "@/types/canvasTypes";
import { Vector2d } from "konva/lib/types";
import { RootState } from "@store/index";

const initialState: CanvasAuxState = {
  draggedRectIdx: null,
  arrowContextMenuIdx: null,
  rectContextMenuIdx: null,
  groupContextMenuId: null,
  selectedRectIndices: [],
  draggedGroupId: null,
  coords: { x: 0, y: 0 },
  stagePosition: { x: 0, y: 0 },
  groupEdgeContextMenu: null,
};

export const canvasAuxSlice = createSlice({
  name: "canvasAux",
  initialState,
  reducers: {
    endDragRectangle: (draft: CanvasAuxState) => {
      draft.draggedRectIdx = null;
    },
    startDragRectangle: (
      draft: CanvasAuxState,
      action: PayloadAction<{ idx: number }>
    ) => {
      draft.draggedRectIdx = action.payload.idx;
    },
    openArrowContextMenu: (
      draft: CanvasAuxState,
      action: PayloadAction<{ idx: [number, number] }>
    ) => {
      draft.arrowContextMenuIdx = action.payload.idx;
    },
    setCoords: (draft: CanvasAuxState, action: PayloadAction<Vector2d>) => {
      draft.coords = action.payload;
    },
    setStagePosition: (
      draft: CanvasAuxState,
      action: PayloadAction<Vector2d>
    ) => {
      draft.stagePosition = action.payload;
    },
    closeArrowContextMenu: (draft: CanvasAuxState) => {
      draft.arrowContextMenuIdx = null;
    },
    openRectContextMenu: (
      draft: CanvasAuxState,
      action: PayloadAction<{ idx: number }>
    ) => {
      draft.rectContextMenuIdx = action.payload.idx;
    },
    closeRectContextMenu: (draft: CanvasAuxState) => {
      draft.rectContextMenuIdx = null;
    },
    openGroupContextMenu: (
      draft: CanvasAuxState,
      action: PayloadAction<{ groupId: string }>
    ) => {
      draft.groupContextMenuId = action.payload.groupId;
    },
    closeGroupContextMenu: (draft: CanvasAuxState) => {
      draft.groupContextMenuId = null;
    },
    openGroupEdgeContextMenu: (
      draft: CanvasAuxState,
      action: PayloadAction<{
        fromGroupId: string;
        toGroupId?: string;
        toRectIdx?: number;
      }>
    ) => {
      draft.groupEdgeContextMenu = action.payload;
    },
    closeGroupEdgeContextMenu: (draft: CanvasAuxState) => {
      draft.groupEdgeContextMenu = null;
    },
    setRectSelection: (
      draft: CanvasAuxState,
      action: PayloadAction<number[]>
    ) => {
      draft.selectedRectIndices = action.payload;
    },
    toggleRectInSelection: (
      draft: CanvasAuxState,
      action: PayloadAction<number>
    ) => {
      const idx = action.payload;
      const i = draft.selectedRectIndices.indexOf(idx);
      if (i === -1) draft.selectedRectIndices.push(idx);
      else draft.selectedRectIndices.splice(i, 1);
    },
    clearRectSelection: (draft: CanvasAuxState) => {
      draft.selectedRectIndices = [];
    },
    startDragGroup: (
      draft: CanvasAuxState,
      action: PayloadAction<{ groupId: string }>
    ) => {
      draft.draggedGroupId = action.payload.groupId;
    },
    endDragGroup: (draft: CanvasAuxState) => {
      draft.draggedGroupId = null;
    },
  },
});

export const {
  startDragRectangle,
  endDragRectangle,
  openArrowContextMenu,
  closeArrowContextMenu,
  openRectContextMenu,
  closeRectContextMenu,
  openGroupContextMenu,
  closeGroupContextMenu,
  openGroupEdgeContextMenu,
  closeGroupEdgeContextMenu,
  setRectSelection,
  toggleRectInSelection,
  clearRectSelection,
  startDragGroup,
  endDragGroup,
  setCoords,
  setStagePosition,
} = canvasAuxSlice.actions;

export const selectCanvas = (state: RootState) => state.canvasAux;

export default canvasAuxSlice.reducer;
